import React, { useEffect, useState } from "react";
import { db, storage } from "../firebase";
import { collection, onSnapshot } from "firebase/firestore";
import { getDownloadURL, ref } from "firebase/storage";

const cardLayouts = [
  [
    {
      className:
        "left-1/2 top-1/2 z-30 -translate-x-1/2 -translate-y-1/2 rotate-1",
      featured: true,
    },
    {
      className:
        "left-[5%] top-[12%] z-20 -rotate-6 hidden sm:block opacity-95",
    },
    {
      className:
        "right-[5%] bottom-[9%] z-20 rotate-6 hidden md:block opacity-95",
    },
  ],
  [
    {
      className:
        "left-1/2 top-1/2 z-30 -translate-x-1/2 -translate-y-1/2 -rotate-2",
      featured: true,
    },
    {
      className:
        "right-[14%] top-[10%] z-20 rotate-5 hidden sm:block opacity-95",
    },
    {
      className:
        "left-[7%] bottom-[8%] z-20 -rotate-4 hidden lg:block opacity-95",
    },
  ],
  [
    {
      className:
        "left-1/2 top-1/2 z-30 -translate-x-1/2 -translate-y-1/2 rotate-2",
      featured: true,
    },
    {
      className:
        "left-[10%] top-[9%] z-20 -rotate-5 hidden sm:block opacity-95",
    },
    {
      className:
        "right-[10%] bottom-[7%] z-20 rotate-4 hidden lg:block opacity-95",
    },
  ],
  [
    {
      className:
        "left-1/2 top-[44%] z-30 -translate-x-1/2 -translate-y-1/2 -rotate-1",
      featured: true,
    },
    {
      className:
        "left-[8%] bottom-[6%] z-20 rotate-5 hidden md:block opacity-95",
    },
    {
      className:
        "right-[7%] top-[8%] z-20 -rotate-5 hidden sm:block opacity-95",
    },
  ],
];

const SLIDESHOW_INTERVAL_MS = 16000;
const PHOTO_WALL_INTERVAL_MS = 20000;

const PhotoCard = ({ entry, featured = false, compact = false, showCaption }) => {
  const sources = [entry.url, ...(entry.fallbackUrls || [])].filter(Boolean);
  const [sourceIndex, setSourceIndex] = useState(0);
  const imageUrl = sources[sourceIndex] || "";
  const imageFailed = !imageUrl;
  const imageClass = `w-full border border-gray-200 bg-gray-100 object-cover ${
    featured
      ? "aspect-[4/5] max-h-[290px] lg:max-h-[315px]"
      : compact
      ? "aspect-square max-h-[155px]"
      : "aspect-[4/5] max-h-[210px]"
  }`;

  useEffect(() => {
    setSourceIndex(0);
  }, [entry.url]);

  return (
    <div
      className={`rounded-md border border-white/80 bg-white shadow-2xl ${
        featured
          ? "w-[230px] px-2.5 pb-4 pt-2.5 sm:w-[285px] lg:w-[310px]"
          : compact
          ? "w-full px-2 pb-3 pt-2"
          : "w-[165px] px-2 pb-3 pt-2 sm:w-[190px] lg:w-[210px]"
      }`}
    >
      {imageFailed ? (
        <div
          className={`${imageClass} flex items-center justify-center px-4 text-center text-xs font-semibold text-gray-500`}
        >
          Photo loading
        </div>
      ) : (
        <img
          src={imageUrl}
          alt="Guest Upload"
          className={imageClass}
          referrerPolicy="no-referrer"
          onError={() =>
            setSourceIndex((index) =>
              index < sources.length - 1 ? index + 1 : sources.length
            )
          }
        />
      )}
      {showCaption && (
        <>
          {entry.message && (
            <div
              className={`text-center italic leading-snug text-gray-800 ${
                featured ? "mt-3 text-sm" : "mt-2 text-xs"
              }`}
            >
              "{entry.message}"
            </div>
          )}
          <div
            className={`mt-2 pr-2 text-right text-gray-500 ${
              featured ? "text-xs" : "text-[11px]"
            }`}
          >
            - {entry.name}
          </div>
        </>
      )}
    </div>
  );
};

const EventSlideshow = ({
  userId,
  eventId,
  layoutMode = "three-photo",
  showCaptions = true,
}) => {
  const [entries, setEntries] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [layoutIndex, setLayoutIndex] = useState(0);

  useEffect(() => {
    if (!userId || !eventId) return undefined;
    let isActive = true;

    const messagesRef = collection(
      db,
      "users",
      userId,
      "events",
      eventId,
      "messages"
    );

    const unsubscribe = onSnapshot(messagesRef, (snapshot) => {
      const approvedPhotoMessages = snapshot.docs
        .map((doc) => ({ id: doc.id, ...doc.data() }))
        .filter(
          (message) =>
            (message.imageUrl || message.imageName) &&
            message.moderationStatus !== "needsReview"
        );

      const approvedPhotos = approvedPhotoMessages.map((message) => ({
        id: message.id,
        imageName: message.imageName || "",
        url: message.imageUrl,
        fallbackUrls: [],
        message: message.message || "Thanks for the memories!",
        name: message.name || "Anonymous",
      }));

      if (isActive) {
        setEntries(approvedPhotos.filter((photo) => photo.url));
      }

      approvedPhotoMessages.forEach(async (message) => {
        if (!message.imageName) return;

        try {
          const storageUrl = await getDownloadURL(
            ref(
              storage,
              `users/${userId}/events/${eventId}/uploads/${message.imageName}`
            )
          );

          if (!isActive) return;

          setEntries((currentEntries) =>
            currentEntries.map((entry) => {
              if (entry.id !== message.id || entry.url === storageUrl) {
                return entry;
              }

              return {
                ...entry,
                url: storageUrl,
                fallbackUrls: [entry.url, ...(entry.fallbackUrls || [])],
              };
            })
          );
        } catch (err) {
          console.warn("Could not refresh slideshow image URL.", err);
        }
      });
    });

    return () => {
      isActive = false;
      unsubscribe();
    };
  }, [userId, eventId]);

  useEffect(() => {
    if (entries.length === 0) return undefined;

    const interval = setInterval(() => {
      const step = layoutMode === "photo-wall" ? 6 : 1;
      setCurrentIndex((index) => (index + step) % entries.length);
      setLayoutIndex((index) => {
        if (cardLayouts.length < 2) return index;

        let next = Math.floor(Math.random() * cardLayouts.length);
        while (next === index) {
          next = Math.floor(Math.random() * cardLayouts.length);
        }
        return next;
      });
    }, layoutMode === "photo-wall" ? PHOTO_WALL_INTERVAL_MS : SLIDESHOW_INTERVAL_MS);

    return () => clearInterval(interval);
  }, [entries.length, layoutMode]);

  if (entries.length === 0) return null;

  if (layoutMode === "photo-wall") {
    const wallEntries = Array.from(
      { length: Math.min(12, entries.length) },
      (_, offset) => entries[(currentIndex + offset) % entries.length]
    );

    return (
      <div className="relative min-h-[430px] w-full overflow-hidden py-4 sm:min-h-[520px] lg:min-h-[560px]">
        <div className="mx-auto grid max-w-5xl grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
          {wallEntries.map((entry, index) => (
            <div
              key={`${entry.url}-${index}-${currentIndex}`}
              className={`${index % 2 === 0 ? "rotate-2" : "-rotate-2"}`}
            >
              <PhotoCard
                entry={entry}
                compact
                showCaption={showCaptions && index < 4}
              />
            </div>
          ))}
        </div>
      </div>
    );
  }

  const visibleCount = layoutMode === "single" ? 1 : Math.min(3, entries.length);
  const visibleEntries = Array.from(
    { length: visibleCount },
    (_, offset) => entries[(currentIndex + offset) % entries.length]
  );
  const layout = cardLayouts[layoutIndex];

  return (
    <div className="relative min-h-[430px] w-full overflow-hidden sm:min-h-[520px] lg:min-h-[560px]">
      {visibleEntries.map((entry, index) => {
        const cardLayout = layoutMode === "single" ? layout[0] : layout[index] || layout[0];

        return (
          <div
            key={`${entry.url}-${index}-${currentIndex}`}
            className={`absolute transform transition-all duration-700 ease-out ${cardLayout.className}`}
          >
            <PhotoCard
              entry={entry}
              featured={cardLayout.featured}
              showCaption={showCaptions}
            />
          </div>
        );
      })}
    </div>
  );
};

export default EventSlideshow;
